import { useDraggable } from "@dnd-kit/core";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { SerializableBlockExport } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { isTemplateExport } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { Typography } from "@circle-react-uikit/Typography";
import { BlockImagePreview } from "./BlockImagePreview";

export interface PreviewProps {
  block: SerializableBlockExport;
  isDisabled?: boolean;
  isElementType?: boolean;
  isOverlay?: boolean;
  name: string;
  onAddBlock: () => void;
}

export const Preview = ({
  name,
  block,
  onAddBlock,
  isElementType = false,
  isOverlay = false,
  isDisabled = false,
}: PreviewProps) => {
  const dragAndDropType = useBuilderStore(state => state.dragAndDropType);
  const isDraggingEnabled = dragAndDropType !== "disabled" && !isDisabled;

  const id = isTemplateExport(block)
    ? block.templateId
    : block.defaultData.type;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: block,
    disabled: isOverlay || isDisabled,
  });

  const handleClick = () => {
    if (!isDragging && !isDisabled) {
      onAddBlock();
    }
  };

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      {...listeners}
      {...attributes}
      className={classNames(
        "border-primary bg-primary hover:border-dark hover:bg-secondary focus:border-dark focus:bg-secondary text-dark relative flex w-full flex-col items-center gap-2 rounded-lg border transition-colors duration-150 ease-linear",
        {
          "cursor-grab": isDraggingEnabled,
          "cursor-grabbing": isDragging,
          "cursor-pointer": !isDraggingEnabled && !isDragging && !isDisabled,
          "cursor-not-allowed opacity-50": isDisabled,
          "opacity-75": isOverlay && !isDisabled,
          "p-4": isElementType,
          "overflow-hidden": !isElementType,
        },
      )}
    >
      <div
        className={classNames("flex items-center justify-center", {
          "opacity-75": isOverlay,
        })}
      >
        <BlockImagePreview
          isElementType={isElementType}
          block={block}
          name={name}
        />
      </div>
      {isElementType && (
        <Typography.LabelXs color="text-dark">{name}</Typography.LabelXs>
      )}
    </button>
  );
};
